import { useState } from 'react';
import { noop } from 'lodash';
import { addToCollection as apiAddToCollection } from '../services/addToCollection';
import { optimisticallyAddToCollection } from '../store/optimisticallyAddToCollection';
import { ResourceType } from '../types';
import { raiseSaveListError, raiseSavedToList } from '../components/toasts/toasts';
import { isCollectionKimeraError } from '../utils/isCollectionKimeraError';
import { useOptionalQueryClient } from './useOptionalQueryClient';
import { INVALIDATE_DELAY } from './constants';

export const useAddToCollection = (resourceType: ResourceType, resourceId: string) => {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useOptionalQueryClient();

    const addToCollection = async ({
        collectionId,
        collectionName,
        collectionSlug,
    }: {
        collectionId: string;
        collectionName: string;
        collectionSlug: string;
    }) => {
        setIsLoading(true);
        let rollback = noop;

        try {
            if (queryClient) {
                rollback = optimisticallyAddToCollection(queryClient, { collectionId, resourceType, resourceId });
            }

            await apiAddToCollection({ collectionId, resourceType, resourceId });
            setIsLoading(false);
            raiseSavedToList(collectionSlug, collectionName);

            setTimeout(() => {
                queryClient?.invalidateQueries({ queryKey: ['collections'] });
            }, INVALIDATE_DELAY);
        } catch (error) {
            rollback();
            if (isCollectionKimeraError(error)) {
                if (error.response?.data?.errorKey === 'item_exists') {
                    raiseSavedToList(collectionSlug, collectionName); // Treat item_exists as success
                } else {
                    raiseSaveListError(error.response?.data.errorKey);
                }
            } else {
                raiseSaveListError(); // Treat unknown error as generic error
            }
            setIsLoading(false);
        }
    };

    return { isLoading, addToCollection };
};
